import useStore from '../../hooks/context/useStore';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import { Button, Skeleton, Tabs, message } from 'antd';
import {
  CloseOutlined,
  CopyOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import useIntersectionObserver from '../../hooks/utility/useIntersectionObserver';
import moment from 'moment-timezone';
import StoreSelector from '../filters/StoreSelector';
import { StoreDetails } from '../../types/store';
import StoreLocation from '../store/StoreLocation';
import ClickToCopy from '../helpers/ClickToCopy';
import collect from 'collect.js';
import useHelpers from '../../hooks/utility/useHelpers';
import useMenu from '../../hooks/context/useMenu';
import useOrderBasket from '../../hooks/context/useOrderBasket';
import str from '../../services/utils/Stringable';
import useCustomerDetails from '../../hooks/context/useCustomerDetails';
import type { TabsProps } from 'antd';
import AddressFormatter from '../../services/utils/address/AddressFormatter';
import { Server } from '../../types/basket';
import ToggleBlockPhoneNumberButton from '../customer/ToggleBlockPhoneNumberButton';
import RestaurantInfo from './sidebar/sections/restaurant-info/RestaurantInfo';
import Reservations from './sidebar/sections/Reservations';
import OrderMethodInfo from './sidebar/sections/order-method-info/OrderMethodInfo';
import Catering from './sidebar/sections/Catering';
import DietaryInfo from './sidebar/sections/DietaryInfo';
import ImportantNotes from './sidebar/sections/ImportantNotes';
import PromotedProducts from './sidebar/sections/PromotedProducts';
import PaymentMethods from './sidebar/sections/PaymentMethods';
import SectionsNavbar from './sidebar/SectionsNavbar';
import { loadCustomerDetails } from '../../services/api/CustomerService';

type Tab = NonNullable<TabsProps['items']>[number];

export const SIDEBAR_WIDTH = 'max(450px,33dvw)';

function StoreTab() {
  const { customerData, setCustomerData } = useCustomerDetails();
  const { storeData, previewStoreData, loadStoreData, setStoreData } = useStore();
  const data = previewStoreData ?? storeData;

  const loadCustomerData = async (storeId: string) => {
    if (!customerData) {
      return;
    }

    return loadCustomerDetails(storeId, { phone_number: customerData.phone_number })
      .then(({ data }) => setCustomerData(data))
      .catch(() => setCustomerData(null));
  };

  const onStoreSelected = async (store: string | StoreDetails) => {
    if (typeof store !== 'string') {
      setStoreData(store);

      return loadStoreData({ id: store.id }).then(() => loadCustomerData(store.id));
    }

    if (store === storeData.id) {
      return;
    }

    return loadStoreData({ id: store }).then(() => loadCustomerData(store));
  };

  return (
    <>
      <section className="pl-none relative space-y-md">
        {Boolean(data.id) && (
          <>
            <div className="sticky top-0 bg-white py-md z-10 border-b">
              <h6>Select store</h6>
              <StoreSelector
                value={storeData.id}
                onChange={(_, object: StoreDetails) => onStoreSelected(object)}
                placeholder="Select store"
                className="my-sm w-full"
              />
              <SectionsNavbar data={data} />
            </div>
            <section className="flex h-32 border-sm rounded-lg overflow-hidden">
              {Boolean(data.branding.cover_image) && (
                <img
                  src={data.branding.cover_image}
                  className="h-full object-cover"
                  alt="Store image"
                />
              )}
              <StoreLocation data={data} />
            </section>
            <RestaurantInfo
              data={data}
              onStoreSelected={onStoreSelected}
              className="border-sm rounded-lg p-lg space-y-3"
            />
            <OrderMethodInfo data={data} className="border-sm rounded-lg p-lg space-y-3" />
            <PaymentMethods data={data} className="border-sm rounded-lg p-lg space-y-3" />
            <Reservations data={data} className="border-sm rounded-lg p-lg space-y-3" />
            <Catering data={data} className="border-sm rounded-lg p-lg space-y-3" />
            <DietaryInfo data={data} className="border-sm rounded-lg p-lg space-y-3" />
            <ImportantNotes
              data={data}
              className="border-sm rounded-lg p-lg space-y-3 bg-amber-400/10 border-amber-400/20"
            />
            <PromotedProducts data={data} className="border-sm rounded-lg p-lg space-y-3" />
          </>
        )}
        {!data.id && (
          <div className="mt-md px-lg py-sm">
            <h6>Select store</h6>
            <StoreSelector
              value={storeData.id}
              onChange={(_, object: StoreDetails) => onStoreSelected(object)}
              placeholder="Select store"
              className="mt-sm w-full"
            />
          </div>
        )}
      </section>
    </>
  );
}

function CustomerTab() {
  const { loadingMenu } = useMenu();
  const { customerData, updateCustomerData } = useCustomerDetails();
  const { compileItem, openLineItemModal } = useOrderBasket();

  const { formatCurrency } = useHelpers();

  const name = str(`${customerData.first_name ?? ''} ${customerData.last_name ?? ''}`)
    .trim()
    .toString();
  const hasDeliveryInfo = useMemo(
    () =>
      Boolean(customerData.delivery_info.street_name) &&
      Boolean(customerData.delivery_info.street_number),
    [customerData.delivery_info.street_name, customerData.delivery_info.street_number],
  );

  const onAddItem = (lineItem: Server.OrderLineItem) =>
    openLineItemModal(
      compileItem({
        ...lineItem,
        quantity: 1,
      }),
    );

  const onBlockToggle = () => {
    message.success(
      customerData.is_blocked
        ? 'Phone number unblocked successfully!'
        : 'Phone number blocked successfully!',
    );

    updateCustomerData({
      is_blocked: !customerData.is_blocked,
    });
  };

  return (
    <div className="h-full pb-sm pr-sm relative">
      {Boolean(name) && <h4 className="mt-sm font-normal">{name}</h4>}
      <div className="flex mt-sm gap-sm">
        <p className="flex text-sm my-auto">
          <PhoneOutlined className="mr-sm text-md text-blue" />
          {customerData.phone_number}
        </p>
        <ClickToCopy value={customerData.phone_number} noIcon>
          <Button
            icon={<CopyOutlined />}
            color="default"
            variant="outlined"
            className="ml-md rounded-2xl"
            onClick={() => navigator.clipboard.writeText(customerData.phone_number)}
          >
            Copy number
          </Button>
        </ClickToCopy>
        <ToggleBlockPhoneNumberButton
          isBlocked={customerData.is_blocked}
          phoneNumber={customerData.phone_number}
          onSuccess={onBlockToggle}
        />
      </div>
      {hasDeliveryInfo && (
        <p className="mt-sm text-sm">
          <EnvironmentOutlined className="mr-sm text-md text-blue" />
          {AddressFormatter.format(customerData.delivery_info)}
        </p>
      )}
      <ul className="list-disc list-inside text-base">
        <li className="mt-sm">
          Online orders: <b>{customerData.online_orders_count}</b>
        </li>
        <li className="mt-sm">
          Phone orders: <b>{customerData.call_center_orders_count}</b>
        </li>
        {customerData.call_center_orders_count > 0 && (
          <li className="mt-sm">
            Call center online payment conversion:{' '}
            <span className="font-semibold">
              {customerData.call_center_online_payment_conversion}%
            </span>
            <span className="ml-xs font-semibold text-[1.3rem]">
              {customerData.call_center_online_payment_conversion > 50 ? '👍' : '😕'}
            </span>
          </li>
        )}
      </ul>
      {Boolean(customerData.last_order) && (
        <>
          {loadingMenu ? (
            <Skeleton active className="mt-md" />
          ) : (
            <>
              <h4 className="mt-sm font text-danger">
                Last order ({moment(customerData.last_order.created_at).fromNow()})
              </h4>
              <div className="mt-sm pb-sm grid grid-cols-3 gap-sm">
                {customerData.last_order.line_items.map((item) => (
                  <button
                    onClick={() => onAddItem(item)}
                    className="flex flex-col justify-between"
                    key={`last-order-item-${item.id}`}
                  >
                    <img
                      src={item.product.image_url}
                      className="w-full bg-gray-100 rounded-xl object-cover aspect-square"
                    />
                    <div>
                      <p className="mt-xs font-medium text-left break-all">{item.product.name}</p>
                      <p className="mt-xs text-sm text-left text-gray-950">
                        {formatCurrency(item.package_unit_price)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default function Sidebar() {
  const { elementViewportHeight } = useIntersectionObserver(document.getElementById('header'));

  const { customerData } = useCustomerDetails();
  const { storeData, previewStoreData, showSidebar, setPreviewStoreData, setShowSidebar } =
    useStore();
  const data = previewStoreData ?? storeData;

  const onClose = () => {
    setShowSidebar(false);
    previewStoreData && setPreviewStoreData(null);
  };

  const tabs = useMemo<Tab[]>(
    () =>
      collect()
        .when(data.id !== null, (collection) =>
          collection.push({
            key: 'store',
            label: 'Store',
            children: <StoreTab />,
          }),
        )
        .when(customerData !== null, (collection) =>
          collection.push({
            key: 'customer',
            label: 'Customer',
            children: <CustomerTab />,
          }),
        )
        .toArray(),
    [data.id, customerData],
  );

  return (
    <>
      <div
        id="sidebar"
        className={classnames(
          'sticky right-0 top-0 z-10 bg-white scrollbar-hide ease-in-out duration-300 h-full space-y-md rounded-lg border-gray-700/20 mr-lg overflow-x-hidden',
          {
            'translate-x-0 border-lg': showSidebar,
            'translate-x-full': !showSidebar,
          },
        )}
        style={{
          height: showSidebar ? `calc(100vh - ${elementViewportHeight}px)` : 0,
          width: showSidebar ? SIDEBAR_WIDTH : 0,
        }}
      >
        <div className="p-lg">
          <Button
            onClick={onClose}
            icon={<CloseOutlined />}
            className="absolute top-sm right-sm z-10"
          />
          {Boolean(data.id) && (
            <Tabs
              items={tabs}
              tabBarStyle={{
                width: 'fit-content',
                marginBottom: 0,
              }}
              className="!mt-none"
            />
          )}
          {!data.id && <StoreTab />}
        </div>
      </div>
      <Button
        onClick={() => setShowSidebar(true)}
        type="text"
        icon={<LeftOutlined />}
        className={classnames('absolute right-xl top-[60px] ease-in-out duration-300 underline', {
          hidden: showSidebar,
        })}
      >
        {data?.name ?? 'Select store'}
      </Button>
    </>
  );
}
